import styled from '@emotion/styled';
import Image from 'next/image';
import { useIsMobile, useIsTablet } from '@hooks/responsive/useIsMobile';
import { primary } from '@styles/Colors';

// components
import Divider from '@components/_atoms/Divider';
import Typography from '@components/_atoms/Typography';

// constants
import { TypoVariant } from '@constants/atoms';
import { MOBILE_MODE, TABLET_MODE } from '@constants/size';
import { useTranslation } from 'next-i18next';
import { PH_COMPANY } from '@constants/company/ph';

type Props = {
  readonly isPh?: boolean;
};

const Content = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 40px;

  @media ${TABLET_MODE} {
    max-width: 768px;
  }

  @media ${MOBILE_MODE} {
    max-width: 320px;
    padding: 0;
  }
`;

const DescriptionBox = styled.div`
  text-align: center;

  display: grid;
  row-gap: 16px;
`;

const GridBox = styled.div`
  margin-top: 80px;

  display: grid;
  row-gap: 40px;

  @media ${TABLET_MODE} {
    margin-top: 40px;
    row-gap: 32px;
  }
`;

const MapList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;

  @media ${MOBILE_MODE} {
    grid-template-columns: none;
    row-gap: 48px;
  }
`;

const SingleMapList = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 528px);
  justify-content: center;
`;

const MapItem = styled.div``;

const MapDescriptionBox = styled.div`
  margin-top: 40px;

  display: grid;
  row-gap: 24px;

  @media ${MOBILE_MODE} {
    margin-top: 16px;
    row-gap: 16px;
  }
`;

const AddressItem = styled.div<{ ko: boolean }>`
  display: grid;
  grid-template-columns: 60px 1fr;

  ${({ ko }) =>
    !ko &&
    `
    display: flex;
    flex-direction: column;
    `}
  /* grid-template-rows: 1fr auto; */


  @media ${TABLET_MODE} {
    grid-template-columns: none;
    row-gap: 8px;
  }
`;

const TextWrapper = styled.div`
  text-align: left;

  display: flex;
  flex-direction: column;
`;

export default function Section2({ isPh = false }: Props) {
  const { t, i18n } = useTranslation('intro');
  const ko = i18n.language === 'ko';

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  return (
    <Content>
      <DescriptionBox>
        <Typography
          variant={isMobile ? TypoVariant.SH4 : isTablet ? TypoVariant.SH3 : TypoVariant.SH2}
          color={primary.blue}>
          Contact Us
        </Typography>
        <Typography
          variant={isMobile ? TypoVariant.SH2 : isTablet ? TypoVariant.H6 : TypoVariant.H5}
          color={primary.gray}>
          {t('section2.title')}
        </Typography>
      </DescriptionBox>

      {isPh ? (
        <GridBox>
          <SingleMapList>
            <MapItem>
              <Image src="/assets/images/intro-map-ph.webp" width={528} height={430} alt="map-ph" />
              <MapDescriptionBox>
                <Typography variant={isMobile ? TypoVariant.SH2 : TypoVariant.SH1}>Metro Manila</Typography>
                <Typography variant={isTablet ? TypoVariant.B3 : TypoVariant.B1}>{PH_COMPANY.address}</Typography>
              </MapDescriptionBox>
            </MapItem>
          </SingleMapList>

          <Divider />

          <DescriptionBox>
            <TextWrapper>
              <Typography variant={isMobile ? TypoVariant.SH2 : TypoVariant.SH1}>E-mail.</Typography>
              <Typography variant={TypoVariant.B3}>{PH_COMPANY.email}</Typography>
            </TextWrapper>
            <TextWrapper>
              <Typography variant={isMobile ? TypoVariant.SH2 : TypoVariant.SH1}>Tel.</Typography>
              <Typography variant={TypoVariant.B3}>{PH_COMPANY.internationalTel}</Typography>
            </TextWrapper>
          </DescriptionBox>
        </GridBox>
      ) : (
        <GridBox>
          <MapList>
            <MapItem>
              <Image src="/assets/images/intro-map-1.webp" width={528} height={430} alt="map-1" />
              <MapDescriptionBox>
                <Typography variant={isMobile ? TypoVariant.SH2 : TypoVariant.SH1}>
                  {t('section2.address1.main-address')}
                </Typography>
                <AddressItem ko={ko}>
                  <Typography variant={isMobile ? TypoVariant.SH3 : TypoVariant.SH2}>
                    {t('section2.address1.laboratory.title')}
                  </Typography>
                  <Typography variant={isTablet ? TypoVariant.B3 : TypoVariant.B1}>
                    {t('section2.address1.laboratory.address')}
                  </Typography>
                </AddressItem>
              </MapDescriptionBox>
            </MapItem>
            <MapItem>
              <Image src="/assets/images/intro-map-2.webp" width={528} height={430} alt="map-1" />
              <MapDescriptionBox>
                <Typography variant={isMobile ? TypoVariant.SH2 : TypoVariant.SH1}>
                  {t('section2.address2.main-address')}
                </Typography>
                <AddressItem ko={ko}>
                  <Typography variant={isMobile ? TypoVariant.SH3 : TypoVariant.SH2}>
                    {t('section2.address2.headquarters.title')}
                  </Typography>
                  <Typography variant={isTablet ? TypoVariant.B3 : TypoVariant.B1}>
                    {t('section2.address2.headquarters.address')}
                  </Typography>
                </AddressItem>
                <AddressItem ko={ko}>
                  <Typography variant={isMobile ? TypoVariant.SH3 : TypoVariant.SH2}>
                    {t('section2.address2.office.title')}
                  </Typography>
                  <Typography variant={isTablet ? TypoVariant.B3 : TypoVariant.B1}>
                    {t('section2.address2.office.address')}
                  </Typography>
                </AddressItem>
              </MapDescriptionBox>
            </MapItem>
          </MapList>

          <Divider />

          <DescriptionBox>
            <TextWrapper>
              <Typography variant={isMobile ? TypoVariant.SH2 : TypoVariant.SH1}>E-mail.</Typography>
              <Typography variant={TypoVariant.B3}>support@marketofmaterial.com</Typography>
            </TextWrapper>
            <TextWrapper>
              <Typography variant={isMobile ? TypoVariant.SH2 : TypoVariant.SH1}>Tel.</Typography>
              <Typography variant={TypoVariant.B3}>+82 1670-1621</Typography>
            </TextWrapper>
          </DescriptionBox>
        </GridBox>
      )}
    </Content>
  );
}
